/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { useState } from 'react';
import { DatePicker } from 'antd';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';

const { RangePicker } = DatePicker;
import { getStoreId } from '../../service/CookieManager';
import { reportType } from '../../constants/AppConstant';
import callApi, { Method } from '../../Api/config';
import {
  CONTACT_FORM,
  BECOME_PARTNER_INFO,
  DOCUMITRA_ORDER_APPLICATION,
  DOCUMITRA_ORDER_TRANSACTION,
  ORDER_USER,
  PHOTO_ORDER_APPLICATION,
  PHOTO_ORDER_TRANSACTION,
  REFUND_REQUEST,
  PARTNER_ORDERS,
} from '../../constants/EntityName';

import './style.scss';
import { toastError, toastSuccess } from '../../service/Toast';

interface IDateRange {
  startDate: string;
  endDate: string;
  storeId?: string;
}

interface IDownlaodProps {
  entityName: string;
  filters?: {
    search?: string;
    status?: string;
    memberStatus?: string;
    serviceName?: string;
    paymentStatus?: string;
    plan?: string;
    addOns?: string | string[];
    [key: string]: any;
  };
}

export const downloadListData = (response: any, entityName: string) => {
  if (response?.status) {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType[entityName]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

export function downloadListDataPDF(response: any, entityName: string) {
  if (response?.status) {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;

    // Convert XLSX data to PDF
    const reader = new FileReader();
    reader.onload = e => {
      if (e.target && e.target.result) {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData: any = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const headers: string[] = jsonData[0].map((header: any) => String(header));
        const rows: any[][] = jsonData.slice(1) as any[][];

        let pdfTitle = '';
        let pdfWidth = 800; // Base width to accommodate all 10 columns properly
        let pdfHeight = 1200; // Increased height for better layout

        if (entityName === PHOTO_ORDER_APPLICATION) {
          pdfTitle = 'Photomitra Order Details';
        } else if (entityName === DOCUMITRA_ORDER_APPLICATION) {
          pdfTitle = 'Documitra Order Details';
        } else if (entityName === ORDER_USER) {
          pdfTitle = 'Documitra Users Details';
        } else if (entityName === CONTACT_FORM || entityName === BECOME_PARTNER_INFO) {
          pdfHeight = 1300;
          pdfTitle =
            entityName === CONTACT_FORM
              ? 'Documitra Contact Details'
              : 'Documitra Become a Partner Details';
        } else if (entityName === PHOTO_ORDER_TRANSACTION) {
          pdfTitle = 'Photomitra Order Transactions';
        } else if (entityName === DOCUMITRA_ORDER_TRANSACTION) {
          pdfTitle = 'Documitra Order Transactions';
        } else if (entityName === REFUND_REQUEST) {
          pdfTitle = 'Refund Request Details';
        }

        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: [pdfWidth, pdfHeight],
        });
        pdf.setFontSize(5); // Further reduced font size to fit more content in columns

        // Calculate required width based on content and adjust if needed
        const requiredWidth = calculateRequiredWidth(headers, rows, pdf);
        if (requiredWidth > pdfWidth) {
          pdfWidth = Math.min(requiredWidth + 100, 1200); // Cap at 1200pt for performance
        }

        // Calculate responsive column widths based on actual content length
        const totalAvailableWidth = pdfWidth - 40; // Leave 20pt margin on each side
        const minColumnWidth = 60; // Minimum width for any column
        const maxColumnWidth = 200; // Maximum width to prevent overly wide columns

        // Calculate column widths based on actual content length
        const columnWidths = headers.map((header, colIndex) => {
          const headerWidth = getTextWidth(pdf, header);

          // Find the maximum content width in this column
          const maxRowWidth = rows.reduce((maxWidth, row) => {
            const cellText = String(row[colIndex] || '--');
            const cellWidth = getTextWidth(pdf, cellText);
            return Math.max(maxWidth, cellWidth);
          }, headerWidth);

          // Calculate responsive width: content width + padding
          const responsiveWidth = Math.max(maxRowWidth + 20, minColumnWidth);

          // Cap the width to prevent overly wide columns
          return Math.min(responsiveWidth, maxColumnWidth);
        });

        // Check if total width exceeds page width
        const totalColumnWidth = columnWidths.reduce((sum, width) => sum + width, 0);

        if (totalColumnWidth > totalAvailableWidth) {
          // Proportionally reduce widths...
          let remainingExcess = totalColumnWidth - totalAvailableWidth;
          let adjustedWidths = [...columnWidths];

          while (remainingExcess > 0) {
            let totalReducible = 0;
            let reducibleColumns = 0;

            adjustedWidths.forEach(width => {
              if (width > minColumnWidth) {
                totalReducible += width - minColumnWidth;
                reducibleColumns++;
              }
            });

            if (totalReducible === 0) break;

            const reductionPerColumn = Math.min(remainingExcess / reducibleColumns, 2);
            adjustedWidths.forEach((width, index) => {
              if (width > minColumnWidth) {
                const reduction = Math.min(reductionPerColumn, width - minColumnWidth);
                adjustedWidths[index] = Math.round(width - reduction);
                remainingExcess -= reduction;
              }
            });
          }
          columnWidths.splice(0, columnWidths.length, ...adjustedWidths);
        }

        // Ensure all rows have the same number of columns, filling missing values with '--'
        const maxColumns = Math.max(...rows.map(row => row.length));
        const formattedRows = rows.map(row => {
          const newRow = Array(maxColumns).fill('--');
          row.forEach((cell, index) => {
            newRow[index] = cell !== undefined ? cell : '--';
          });
          return newRow;
        });

        // Define table dimensions
        const startX = 20;
        const startY = 20;
        const cellHeight = 12;
        const lineHeight = 8;
        const headerMargin = 12;

        let currentY = startY;

        // Draw title text
        const titleText = pdfTitle;
        const titleX = pdfHeight / 3;
        pdf.setFontSize(20);
        pdf.text(titleText, titleX, currentY, { align: 'center' });

        currentY += cellHeight;

        drawTableHeaders(pdf, headers, columnWidths, startX, currentY, cellHeight, lineHeight);

        currentY += headerMargin;

        // Draw table rows with pagination
        formattedRows.forEach((row, rowIndex) => {
          if (rowIndex > 0 && rowIndex % 15 === 0) {
            pdf.addPage();
            currentY = startY;
            drawTableHeaders(pdf, headers, columnWidths, startX, currentY, cellHeight, lineHeight);
            currentY += headerMargin;
          }
          currentY = drawTableRow(
            pdf,
            row,
            columnWidths,
            startX,
            currentY,
            headers,
            cellHeight,
            lineHeight
          );
        });

        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);

        a.href = pdfUrl;
        a.download = `${reportType[entityName]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    };
    reader.readAsArrayBuffer(response.data);
  }
}

// Reusable drawing functions
export function calculateRequiredWidth(headers: string[], rows: any[][], pdf: any): number {
  const minColumnWidth = 60;
  const padding = 20;
  const totalWidth = headers.reduce((total, header, colIndex) => {
    const headerWidth = getTextWidth(pdf, header);
    const maxRowWidth = rows.reduce((maxWidth, row) => {
      const cellText = String(row[colIndex] || '--');
      const cellWidth = getTextWidth(pdf, cellText);
      return Math.max(maxWidth, cellWidth);
    }, headerWidth);
    return total + Math.max(maxRowWidth + padding, minColumnWidth);
  }, 0);
  return totalWidth + 40;
}

export function getTextWidth(pdf: any, text: string) {
  return (pdf.getStringUnitWidth(text) * pdf.internal.getFontSize()) / pdf.internal.scaleFactor;
}

export function drawTableHeaders(
  pdf: any,
  tableHeader: string[],
  columnWidths: number[],
  startX: number,
  currentY: number,
  lineHeight: number,
  cellHeight: number
) {
  pdf.setFontSize(6);
  pdf.setFont('helvetica', 'bold');
  pdf.setFillColor(200, 200, 200);
  pdf.rect(startX, currentY, getTableWidth(columnWidths), cellHeight + 4, 'F');
  pdf.setDrawColor(100, 100, 100);
  pdf.setLineWidth(0.3);
  pdf.line(startX, currentY, startX + getTableWidth(columnWidths), currentY);
  pdf.line(
    startX,
    currentY + cellHeight + 4,
    startX + getTableWidth(columnWidths),
    currentY + cellHeight + 4
  );
  pdf.line(startX, currentY, startX, currentY + cellHeight + 4);
  let currentX = startX;
  tableHeader.forEach((header, index) => {
    if (index > 0) {
      pdf.line(currentX, currentY, currentX, currentY + cellHeight + 4);
    }
    currentX += columnWidths[index];
  });
  pdf.line(
    startX + getTableWidth(columnWidths),
    currentY,
    startX + getTableWidth(columnWidths),
    currentY + cellHeight + 4
  );
  tableHeader.forEach((header, index) => {
    const headerX = startX + getPreviousCellWidth(columnWidths, index) + 10;
    const headerY = currentY + (cellHeight + 4) / 2 + 2;
    pdf.text(header, headerX, headerY);
  });
}

export function drawTableRow(
  pdf: any,
  row: any[],
  columnWidths: number[],
  startX: number,
  currentY: number,
  headers: string[],
  lineHeight: number,
  cellHeight: number
) {
  let currentX = startX;
  let maxCellHeight = cellHeight;
  row.forEach((cell, cellIndex) => {
    const cellText = cell === undefined ? '--' : String(cell);
    let lines: string[];
    if (cellIndex === 5) {
      lines = formatMemberStatuses(cellText, columnWidths[cellIndex] - 8, pdf);
    } else {
      lines = splitTextIntoLines(cellText, columnWidths[cellIndex] - 8, pdf);
    }
    const cellTextHeight = lines.length * lineHeight;
    maxCellHeight = Math.max(maxCellHeight, cellTextHeight + 4);
  });
  row.forEach((cell, cellIndex) => {
    const cellText = cell === undefined ? '--' : String(cell);
    const cellWidth = columnWidths[cellIndex];
    let lines: string[];
    if (cellIndex === 5) {
      lines = formatMemberStatuses(cellText, cellWidth - 8, pdf);
    } else {
      lines = splitTextIntoLines(cellText, cellWidth - 8, pdf);
    }
    pdf.setDrawColor(100, 100, 100);
    pdf.setLineWidth(0.3);
    pdf.rect(currentX, currentY, cellWidth, maxCellHeight);
    const paddingX = 4;
    const paddingY = 2;
    const textX = currentX + paddingX;
    const textY = currentY + paddingY + lineHeight;
    lines.forEach((line, lineIndex) => {
      const textYLine = textY + lineIndex * lineHeight;
      pdf.text(line, textX, textYLine);
    });
    currentX += cellWidth;
  });
  return currentY + maxCellHeight;
}

export function splitTextIntoLines(text: string, maxWidth: number, pdf: any): string[] {
  if (!text || maxWidth <= 0) return [text || ''];
  const lineBreakSplits = text.split('\n');
  const allLines: string[] = [];
  lineBreakSplits.forEach(lineBreakSplit => {
    const trimmedLine = lineBreakSplit.trim();
    if (!trimmedLine) return;
    if (getTextWidth(pdf, trimmedLine) <= maxWidth) {
      allLines.push(trimmedLine);
      return;
    }
    const words = trimmedLine.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (getTextWidth(pdf, testLine) <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          if (getTextWidth(pdf, word) > maxWidth) {
            let partialWord = '';
            for (let i = 0; i < word.length; i++) {
              const testChar = partialWord + word[i];
              if (getTextWidth(pdf, testChar) <= maxWidth) {
                partialWord = testChar;
              } else {
                if (partialWord) lines.push(partialWord);
                partialWord = word[i];
              }
            }
            if (partialWord) lines.push(partialWord);
          } else {
            lines.push(word);
          }
        }
      }
    });
    if (currentLine) {
      lines.push(currentLine);
    }
    allLines.push(...lines);
  });
  return allLines;
}

export function formatMemberStatuses(text: string, maxWidth: number, pdf: any): string[] {
  if (!text || text === 'No Members') {
    return [text || 'No Members'];
  }
  const lineBreakSplits = text.split('\n');
  const allLines: string[] = [];
  lineBreakSplits.forEach((lineBreakSplit, index) => {
    const trimmedStatus = lineBreakSplit.trim();
    if (trimmedStatus) {
      if (index > 0) {
        allLines.push('');
      }
      const statusLines = splitTextIntoLines(trimmedStatus, maxWidth, pdf);
      allLines.push(...statusLines);
    }
  });
  return allLines;
}

export function getTableWidth(columnWidths: number[]): number {
  return columnWidths.reduce((totalWidth, width) => totalWidth + width, 0);
}

export function getPreviousCellWidth(columnWidths: number[], index: number): number {
  return columnWidths.slice(0, index).reduce((totalWidth, width) => totalWidth + width, 0);
}

const DownloadData = (props: IDownlaodProps) => {
  const { entityName, filters } = props;
  const userStoreId = getStoreId() || null;
  const [dateError, setDateError] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<IDateRange>({
    startDate: '',
    endDate: '',
    storeId: '',
  });

  const isDateDisabled = (current: moment.Moment | null) =>
    current ? current > moment().endOf('day') : false;

  const dateSelector = (dateRangeArray: any) => {
    if (dateRangeArray && dateRangeArray.length >= 2) {
      const startDateRange = dateRangeArray[0]?.toISOString();
      const endDateRange = dateRangeArray[1]?.toISOString();
      setDateRange({
        endDate: endDateRange,
        startDate: startDateRange,
        ...(userStoreId && { storeId: userStoreId }),
      });
    } else {
      console.error('Date range array is null or does not have enough elements.');
    }
  };

  const generateReport = async () => {
    if (!dateRange.endDate || !dateRange.startDate) {
      setDateError(true);
      return;
    }

    // Map the entityName to the correct report type
    let reportTypeValue;
    if (entityName === DOCUMITRA_ORDER_TRANSACTION) {
      reportTypeValue = 'DocumitraTransaction';
    } else if (entityName === PHOTO_ORDER_TRANSACTION) {
      reportTypeValue = 'PhotomitraTransaction';
    } else if (entityName === DOCUMITRA_ORDER_APPLICATION) {
      reportTypeValue = 'DocumitraOrder';
    } else if (entityName === PHOTO_ORDER_APPLICATION) {
      reportTypeValue = 'PhotomitraOrder';
    } else if (entityName === CONTACT_FORM) {
      reportTypeValue = 'UserInfo';
    } else if (entityName === BECOME_PARTNER_INFO) {
      reportTypeValue = 'BecomePartnerInfo';
    } else if (entityName === ORDER_USER) {
      reportTypeValue = 'Users';
    } else if (entityName === REFUND_REQUEST) {
      reportTypeValue = 'RefundRequest';
    } else if (entityName === PARTNER_ORDERS) {
      reportTypeValue = 'PartnerOrder';
    }

    if (!reportTypeValue) {
      toastError('Invalid report type');
      return;
    }

    const queryParams: any = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      reportType: reportTypeValue,
    };

    if (filters) {
      if (filters.search) queryParams.search = filters.search;
      if (filters.status) queryParams.status = filters.status;
      if (filters.memberStatus) queryParams.memberStatus = filters.memberStatus;
      if (filters.serviceName) queryParams.serviceName = filters.serviceName;
      if (filters.paymentStatus) queryParams.paymentStatus = filters.paymentStatus;
      if (filters.plan) queryParams.plan = filters.plan;
    }

    const res = await callApi(
      `/admin/download-reports`,
      'GET',
      null,
      queryParams,
      {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      Method.DOWNLOAD
    );

    if (res?.statusCode && res.statusCode >= 400) {
      const errorMessage =
        res.data?.message || res.data?.error || `Date range cannot exceed three months.`;
      toastError(errorMessage);
      return;
    }

    if (res?.status && res?.data) {
      toastSuccess(`Download will start shortly`);
      downloadListData(res, entityName);
      setDateError(false);
    } else {
      toastError(`Failed to download. Please try again.`);
    }
  };

  const generateReportPDF = async () => {
    if (!dateRange.endDate || !dateRange.startDate) {
      setDateError(true);
      return;
    }

    // Logic similar to generateReport but calling downloadListDataPDF
    let reportTypeValue;
    if (entityName === DOCUMITRA_ORDER_TRANSACTION) reportTypeValue = 'DocumitraTransaction';
    else if (entityName === PHOTO_ORDER_TRANSACTION) reportTypeValue = 'PhotomitraTransaction';
    else if (entityName === DOCUMITRA_ORDER_APPLICATION) reportTypeValue = 'DocumitraOrder';
    else if (entityName === PHOTO_ORDER_APPLICATION) reportTypeValue = 'PhotomitraOrder';
    else if (entityName === CONTACT_FORM) reportTypeValue = 'UserInfo';
    else if (entityName === BECOME_PARTNER_INFO) reportTypeValue = 'BecomePartnerInfo';
    else if (entityName === ORDER_USER) reportTypeValue = 'Users';
    else if (entityName === REFUND_REQUEST) reportTypeValue = 'RefundRequest';
    else if (entityName === PARTNER_ORDERS) reportTypeValue = 'PartnerOrder';

    if (!reportTypeValue) {
      toastError('Invalid report type');
      return;
    }

    const queryParams: any = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      reportType: reportTypeValue,
    };

    const res = await callApi(
      `/admin/download-reports`,
      'GET',
      null,
      queryParams,
      {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      Method.DOWNLOAD
    );

    if (res?.statusCode && res.statusCode >= 400) {
      const errorMessage =
        res.data?.message || res.data?.error || `Date range cannot exceed three months.`;
      toastError(errorMessage);
      return;
    }

    if (res?.status && res?.data) {
      downloadListDataPDF(res, entityName);
      setDateError(false);
    } else {
      toastError(`Failed to download. Please try again.`);
    }
  };

  return (
    <div className="mainContainer">
      <div className="rangeContainer" style={{ marginLeft: '10px' }}>
        <RangePicker
          size="small"
          ranges={{
            Today: [moment(), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          onChange={dateSelector}
          disabledDate={isDateDisabled}
        />
        <p
          id="errorText"
          style={{
            color: 'red',
            visibility: dateError ? 'visible' : 'hidden',
          }}
        >
          Please select the date range.
        </p>
      </div>
      <div className="errorText">
        <button className="formDownloadBtn" onClick={generateReport}>
          Download Excel
        </button>
        <button className="formDownloadBtn" onClick={generateReportPDF}>
          Download PDF
        </button>
      </div>
    </div>
  );
};

export default DownloadData;
